import * as React from "react"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

/** Una opción del combobox. `pista` es la línea secundaria que desambigua. */
export interface OpcionDeCombobox {
  valor: string
  etiqueta: string
  pista?: string
  /**
   * El código de la opción, mostrado a la DERECHA de la fila y buscable como
   * la etiqueta.
   *
   * Va aparte de `valor` aunque muchas veces coincida: `valor` es la identidad
   * que el componente devuelve y no siempre es algo que un humano deba ver
   * (un id autoincremental no le dice nada a nadie). Este campo declara
   * explícitamente que el código ES parte de lo que el usuario lee y escribe.
   */
  codigo?: string
}

/**
 * Deja el texto comparable: descompone cada letra acentuada y descarta la
 * marca. En Chile no es un lujo, es la diferencia entre encontrar y no
 * encontrar: nadie escribe los acentos al buscar, así que sin esto "curico" no
 * llega a Curicó, "vina" no llega a Viña del Mar y "nuble" no llega a Ñuble.
 *
 * La ñ también se aplana a n —NFD la separa en `n` + virgulilla, y la
 * virgulilla cae con el resto—, que es justamente lo que hace que "nuble" y
 * "ñuble" encuentren la misma comuna. Para BUSCAR está bien; nada de esto toca
 * el nombre que se muestra ni el que se guarda.
 */
function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
}

/**
 * Extiende las props del `<button>` disparador para que lo que le inyecte un
 * envoltorio llegue hasta él. No es genérico por gusto: `FormControl` del DS
 * mete `id`, `aria-describedby` y `aria-invalid` por un `Slot`, y sin este
 * pasamanos el `<FormLabel>` apunta a un id que no existe — el campo se queda
 * sin etiqueta accesible y sin el mensaje de error asociado.
 */
interface ComboboxProps
  extends Omit<React.ComponentProps<"button">, "value" | "onChange" | "type"> {
  opciones: OpcionDeCombobox[]
  /** El valor elegido, o `null` cuando todavía no hay ninguno. */
  valor: string | null
  onValorChange: (valor: string | null) => void
  placeholder?: string
  placeholderBusqueda?: string
  mensajeVacio?: string
  /** Se refleja como `aria-invalid` para que herede el estilo de error del DS. */
  invalido?: boolean
}

/**
 * Selector con buscador: un disparador con forma de Input y un popover con la
 * lista filtrable.
 *
 * Existe para los maestros que no caben en un `NativeSelect`. La regla práctica
 * del DS: hasta ~15 opciones va el select nativo, que es más simple y accesible
 * de fábrica; por encima, una lista sin buscador obliga a recorrer con la rueda
 * y es donde este componente empieza a ganar.
 *
 * FILTRA ACÁ, no en el servidor. Está pensado para maestros que caben enteros
 * en memoria (las 346 comunas del país pesan ~25 KB): así el listado responde
 * en la tecla, sin debounce ni una consulta por pulsación.
 *
 * `shouldFilter={false}`: el filtro propio de cmdk no ignora los acentos y
 * ordena por su puntaje difuso, que con nombres de lugares propone
 * coincidencias raras antes que la obvia. Acá el orden es explícito — primero
 * lo que EMPIEZA con lo escrito, después lo que lo contiene — que es lo que
 * espera quien tipea las primeras letras de una comuna.
 */
function Combobox({
  opciones,
  valor,
  onValorChange,
  placeholder = "Selecciona una opción",
  placeholderBusqueda = "Buscar…",
  mensajeVacio = "Sin resultados.",
  disabled,
  invalido,
  className,
  ...resto
}: ComboboxProps) {
  const [abierto, setAbierto] = React.useState(false)
  const [busqueda, setBusqueda] = React.useState("")

  const seleccionada = React.useMemo(
    () => opciones.find((opcion) => opcion.valor === valor) ?? null,
    [opciones, valor],
  )

  const filtradas = React.useMemo(() => {
    const consulta = normalizar(busqueda)
    if (consulta === "") return opciones

    const empiezan: OpcionDeCombobox[] = []
    const contienen: OpcionDeCombobox[] = []

    for (const opcion of opciones) {
      const etiqueta = normalizar(opcion.etiqueta)
      const codigo = normalizar(opcion.codigo ?? "")

      // El CÓDIGO rankea igual que el nombre, no por debajo: quien lo teclea
      // ya sabe cuál quiere —lo está copiando de un documento— así que su
      // opción tiene que salir arriba, no detrás de las que apenas lo
      // contienen por el medio.
      if (etiqueta.startsWith(consulta) || (codigo !== "" && codigo.startsWith(consulta))) {
        empiezan.push(opcion)
      } else if (
        etiqueta.includes(consulta) ||
        codigo.includes(consulta) ||
        // La pista entra en la búsqueda para que se pueda llegar por región o
        // provincia ("Ñuble") y no solo por el nombre exacto.
        normalizar(opcion.pista ?? "").includes(consulta)
      ) {
        contienen.push(opcion)
      }
    }

    return [...empiezan, ...contienen]
  }, [opciones, busqueda])

  return (
    <Popover
      open={abierto}
      onOpenChange={(siguiente) => {
        setAbierto(siguiente)
        // La búsqueda se limpia al cerrar: si quedara escrita, reabrir el
        // selector mostraría la lista ya filtrada por lo que se tipeó la vez
        // anterior, y desde afuera eso se ve como un maestro incompleto.
        if (!siguiente) setBusqueda("")
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={abierto}
          aria-invalid={invalido}
          disabled={disabled}
          data-slot="combobox-trigger"
          // Las mismas proporciones que `Input` y `NativeSelect` (h-11, borde
          // 1.5px, radio 12px, px-3.5), escritas acá por el mismo motivo que
          // en `NativeSelect`: al lado de un Input del DS, las medidas stock de
          // un Button se ven como otro control.
          className={cn(
            "border-input dark:bg-input/30 dark:hover:bg-input/50 flex h-11 w-full min-w-0 items-center justify-between gap-2 rounded-md border-[1.5px] bg-campo px-3.5 py-2 text-left text-sm font-semibold transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
            "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
            "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
            className,
          )}
          // Va ÚLTIMO para que lo que inyecta `FormControl` (`id`,
          // `aria-describedby`, `aria-invalid`) gane sobre el valor propio: en
          // un formulario, quién está en error lo sabe el formulario.
          {...resto}
        >
          <span className={cn("truncate", !seleccionada && "text-muted-foreground")}>
            {seleccionada?.etiqueta ?? placeholder}
          </span>
          <ChevronsUpDownIcon
            className="text-muted-foreground size-4 shrink-0 opacity-50"
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>

      {/* `p-0` pisa el `p-4` del popover: acá el contenido es una lista que
          llega hasta el borde. El ancho sigue al del disparador para que el
          panel no se despegue de la columna del formulario. */}
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholderBusqueda}
            value={busqueda}
            onValueChange={setBusqueda}
          />
          <CommandList>
            <CommandEmpty>{mensajeVacio}</CommandEmpty>
            <CommandGroup>
              {filtradas.map((opcion) => (
                <CommandItem
                  key={opcion.valor}
                  value={opcion.valor}
                  onSelect={() => {
                    // Volver a elegir lo ya elegido LIMPIA la selección. Es la
                    // única forma de dejar el campo vacío otra vez, y hay
                    // motivo para necesitarla: un borrador se guarda incompleto
                    // y alguien puede haber elegido la comuna equivocada antes
                    // de tener el dato.
                    onValorChange(opcion.valor === valor ? null : opcion.valor)
                    setAbierto(false)
                  }}
                >
                  <CheckIcon
                    className={cn(
                      "size-4 shrink-0",
                      opcion.valor === valor ? "opacity-100" : "opacity-0",
                    )}
                    aria-hidden="true"
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{opcion.etiqueta}</span>
                    {opcion.pista ? (
                      <span className="text-ink-hint truncate text-xs">{opcion.pista}</span>
                    ) : null}
                  </span>
                  {/* A la DERECHA y no pegado al nombre: así los códigos quedan
                      en columna y se recorren de un vistazo, que es como se
                      busca uno que se está copiando de un documento. Pegado al
                      nombre, cada uno arrancaría en una sangría distinta.

                      `tabular-nums` fija el ancho de los dígitos —si no, un
                      "11111" es más angosto que un "00000" y la columna
                      tiembla—, y `shrink-0` lo protege de la comuna de nombre
                      largo, que si no lo comprimiría hasta cortarlo. */}
                  {opcion.codigo ? (
                    <span className="text-ink-hint shrink-0 text-xs tabular-nums">
                      {opcion.codigo}
                    </span>
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export { Combobox }
